import React, { useState, useEffect, useCallback } from 'react'
import { Play, Pause, Square, Trash2, Edit3, ChevronDown, ChevronRight } from 'lucide-react'
import usePlayStore from '../../stores/playStore'

const AnimationEditor = () => {
  const [selectedTeam, setSelectedTeam] = useState(null)
  const [selectedProp, setSelectedProp] = useState(null)
  const [isRecording, setIsRecording] = useState(false)
  const [isCountdown, setIsCountdown] = useState(false)
  const [countdownValue, setCountdownValue] = useState(0)
  const [recordingStartTime, setRecordingStartTime] = useState(0)
  const [recordingPath, setRecordingPath] = useState([])
  const [recordingElapsedTime, setRecordingElapsedTime] = useState(0)
  const [recordingTimelineStartTime, setRecordingTimelineStartTime] = useState(0)
  const [expandedGroups, setExpandedGroups] = useState({}) // Track which groups are expanded
  const [editingNumber, setEditingNumber] = useState(null) // Track which team's number is being edited
  const [numberInputValue, setNumberInputValue] = useState('') // Temporary value while editing
  
  const { 
    teams, 
    props,
    animations, 
    currentTime, 
    duration,
    isPlaying,
    addAnimation, 
    removeAnimation,
    updateAnimation,
    clearAnimations,
    setCurrentTime,
    setDuration,
    setPlaying,
    updateTeam,
    updateProp,
    selectedEntity,
    setSelectedEntity,
    clearSelectedEntity
  } = usePlayStore()

  const stopRecording = useCallback(() => {
    if (!isRecording || (!selectedTeam && !selectedProp)) return
    
    let target = null
    let isProp = false
    
    if (selectedProp) {
      target = props.find(p => p.id === selectedProp)
      isProp = true
    } else if (selectedTeam) {
      target = teams.find(t => t.id === selectedTeam)
      isProp = false
    }
    
    if (!target) return
    
    // Get the recorded path from the scene
    let recordedPath = recordingPath
    let finalX = target.x
    let finalY = target.y
    
    if (window.phaserScene) {
      const scenePath = window.phaserScene.stopRecording()
      if (scenePath && scenePath.length > 0) {
        recordedPath = scenePath
        // Get the final position from the last recorded point
        const lastPoint = scenePath[scenePath.length - 1]
        if (lastPoint && typeof lastPoint.x === 'number' && typeof lastPoint.y === 'number') {
          finalX = lastPoint.x
          finalY = lastPoint.y
        }
      }
    }
    
    // Validate the recorded path before creating animation
    if (recordedPath && recordedPath.length > 0 && recordedPath[0] && 
        typeof recordedPath[0].x === 'number' && typeof recordedPath[0].y === 'number') {
      
       // Calculate proper timing - use the timeline time when recording started
       const recordingDuration = (Date.now() - recordingStartTime) / 1000 // Convert to seconds
       const animationStartTime = recordingTimelineStartTime // Use the timeline time when recording started
       const animationEndTime = animationStartTime + recordingDuration
      
      // Create animation from recorded path
      const animationData = {
        type: 'move',
        startTime: animationStartTime,
        endTime: animationEndTime,
        startX: recordedPath[0].x,
        startY: recordedPath[0].y,
        endX: finalX,
        endY: finalY,
        path: recordedPath
      }
      
                if (isProp) {
                  animationData.propId = selectedProp
                } else {
                  animationData.teamId = selectedTeam
                }
      
      addAnimation(animationData)
    }
    
     setIsRecording(false)
     setSelectedTeam(null)
     setSelectedProp(null)
     setRecordingPath([])
     setRecordingTimelineStartTime(0)
     
     // Clear focus effects
     if (window.phaserScene) {
       window.phaserScene.setFocus(null, null)
     }
     
     // Stop playback and reset timeline to 0s after recording
     setPlaying(false)
     setCurrentTime(0)
     
     // Update the store position to match the final recorded position
     if (window.phaserScene) {
       const teamSprite = window.phaserScene.teams.find(t => t.teamData?.id === selectedTeam)
       const propSprite = window.phaserScene.props.find(p => p.propData?.id === selectedProp)
       
       if (teamSprite) {
         updateTeam(selectedTeam, { x: finalX, y: finalY })
       } else if (propSprite) {
         updateProp(selectedProp, { x: finalX, y: finalY })
       }
     }
  }, [isRecording, selectedTeam, selectedProp, recordingPath, recordingTimelineStartTime, addAnimation, setPlaying, setCurrentTime, updateTeam, updateProp])

  // Timer effect for recording elapsed time
  useEffect(() => {
    let interval = null
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingElapsedTime(Date.now() - recordingStartTime)
      }, 100) // Update every 100ms
    } else {
      setRecordingElapsedTime(0)
    }
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [isRecording, recordingStartTime])

  // Auto-stop recording when total duration would exceed timeline duration
  useEffect(() => {
    if (isRecording && recordingElapsedTime > 0) {
      // Calculate total duration: existing animations + current recording
      const currentRecordingDuration = recordingElapsedTime / 1000 // Convert to seconds
      const totalDuration = recordingTimelineStartTime + currentRecordingDuration
      
      // Stop if total duration would exceed timeline duration
      if (totalDuration >= duration) {
        stopRecording()
      }
    }
  }, [isRecording, recordingElapsedTime, recordingTimelineStartTime, duration, stopRecording])

  // Countdown effect
  useEffect(() => {
    console.log('Countdown effect triggered:', { isCountdown, countdownValue, selectedTeam, selectedProp })
    let interval = null
    if (isCountdown && countdownValue > 0) {
      interval = setInterval(() => {
        setCountdownValue(prev => {
          console.log('Countdown tick:', prev)
          if (prev <= 1) {
            // Don't set isCountdown to false here, let the next effect handle it
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [isCountdown, countdownValue])

  // Separate effect to handle countdown completion
  useEffect(() => {
    if (isCountdown && countdownValue === 0) {
      // Countdown finished, start recording
      console.log('Countdown finished, starting recording for:', selectedTeam || selectedProp)
      setIsCountdown(false)
      setIsRecording(true)
      setRecordingStartTime(Date.now())
      
      // Start Phaser recording
      if (window.phaserScene && (selectedTeam || selectedProp)) {
        const targetId = selectedTeam || selectedProp
        console.log('Starting Phaser recording for:', targetId)
        window.phaserScene.startRecording(targetId)
      } else {
        console.log('Cannot start Phaser recording - scene or target not available')
      }
      
      // Resume animations - all existing animations will now play along with the new recording
      if (animations.length > 0) {
        setPlaying(true)
      }
    }
  }, [isCountdown, countdownValue, selectedTeam, selectedProp, animations.length])

  // Auto-scroll to selected entity when it changes
  useEffect(() => {
    if (selectedEntity) {
      // Find the corresponding element in the DOM
      const elementId = selectedEntity.id
      const element = document.getElementById(`animatable-element-${elementId}`)
      
      if (element) {
        // Scroll the element into view with smooth behavior
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        })
      }
    }
  }, [selectedEntity])

  const startRecording = (targetId, isProp = false) => {
    // Clear any selected entity from field clicks
    clearSelectedEntity()
    
    let target = null
    if (isProp) {
      target = props.find(p => p.id === targetId)
      setSelectedProp(targetId)
      setSelectedTeam(null)
    } else {
      target = teams.find(t => t.id === targetId)
      setSelectedTeam(targetId)
      setSelectedProp(null)
    }
    
    if (!target) return
    
    // Find existing animations for this team/prop
    const existingAnimations = animations.filter(anim => 
      isProp ? anim.propId === targetId : anim.teamId === targetId
    )
    
    // Calculate the start time for the new animation
    let timelineStartTime = currentTime // Default to current timeline time
    
    if (existingAnimations.length > 0) {
      // Find the latest animation end time
      const latestEndTime = Math.max(...existingAnimations.map(anim => anim.endTime))
      timelineStartTime = latestEndTime // Start where the last animation ended
    }
    
    // Move the timeline to the start time of the new animation
    setCurrentTime(timelineStartTime)
    
    // Start countdown instead of immediately recording
    setRecordingTimelineStartTime(timelineStartTime)
    setRecordingPath([{ x: target.x, y: target.y, time: 0 }])
    
    // Start 2-second countdown
    setIsCountdown(true)
    setCountdownValue(2)
    
    // Set focus on the target for visual indication
    if (window.phaserScene) {
      if (isProp) {
        window.phaserScene.setFocus(null, targetId)
      } else {
        window.phaserScene.setFocus(targetId, null)
      }
    }
    
    // Pause all animations during countdown - they will resume when recording starts
    if (isPlaying) {
      setPlaying(false)
    }
  }



  const deleteAnimation = (animationId) => {
    // Find the animation before deleting to get its starting position
    const deletedAnimation = animations.find(anim => anim.id === animationId)
    if (!deletedAnimation) {
      removeAnimation(animationId)
      return
    }
    
    // Get the original starting position
    const restoreX = deletedAnimation.startX
    const restoreY = deletedAnimation.startY
    
    // If playback is running, stop it and reset timeline to 0 first
    if (isPlaying) {
      setPlaying(false)
      setCurrentTime(0)
    }
    
    // Remove the animation from the store
    removeAnimation(animationId)
    
    // Restore sprite to its original starting position directly in Phaser scene
    if (window.phaserScene) {
      const scene = window.phaserScene
      
      if (deletedAnimation.teamId) {
        // Find and update the team sprite directly
        const teamSprite = scene.teams.find(t => t.teamData?.id === deletedAnimation.teamId)
        if (teamSprite) {
          teamSprite.x = restoreX
          teamSprite.y = restoreY
          
          // Update number text position
          if (teamSprite.numberText) {
            teamSprite.numberText.x = restoreX
            teamSprite.numberText.y = restoreY
          }
          
          // Update selection border if selected
          if (teamSprite.selectionBorder) {
            teamSprite.selectionBorder.clear()
            teamSprite.selectionBorder.lineStyle(3, 0x00FF00, 1)
            teamSprite.selectionBorder.strokeCircle(restoreX, restoreY, 20)
          }
        }
      } else if (deletedAnimation.propId) {
        // Find and update the prop sprite directly
        const propSprite = scene.props.find(p => p.propData?.id === deletedAnimation.propId)
        if (propSprite) {
          propSprite.x = restoreX
          propSprite.y = restoreY
          
          // Update selection border if selected
          if (propSprite.selectionBorder) {
            propSprite.selectionBorder.clear()
            propSprite.selectionBorder.lineStyle(3, 0x00FF00, 1)
            
            if (propSprite.propData?.type === 'cone') {
              propSprite.selectionBorder.strokeCircle(restoreX - 10, restoreY - 13, 18)
            } else {
              propSprite.selectionBorder.strokeCircle(restoreX, restoreY, 20)
            }
          }
        }
      }
    }
    
    // Also update in store to keep it in sync
    if (deletedAnimation.teamId) {
      updateTeam(deletedAnimation.teamId, { x: restoreX, y: restoreY })
    } else if (deletedAnimation.propId) {
      updateProp(deletedAnimation.propId, { x: restoreX, y: restoreY })
    }
  }

  const getTeamAnimations = (teamId) => {
    return animations.filter(anim => anim.teamId === teamId)
  }

  const getPropAnimations = (propId) => {
    return animations.filter(anim => anim.propId === propId)
  }

  const getElementAnimations = (elementId, isProp = false) => {
    if (isProp) {
      return getPropAnimations(elementId)
    } else {
      return getTeamAnimations(elementId)
    }
  }

  const getElementName = (elementId) => {
    const team = teams.find(t => t.id === elementId)
    if (team) {
      return `${team.type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())} #${team.number}`
    }
    const prop = props.find(p => p.id === elementId)
    if (prop) {
      return prop.type === 'soccer-ball' ? 'Soccer Ball' : 'Cone'
    }
    return 'Unknown'
  }

  const handleNumberChange = (teamId, newNumber) => {
    const team = teams.find(t => t.id === teamId)
    if (!team) return

    // Validate number
    const numValue = parseInt(newNumber)
    if (isNaN(numValue) || numValue < 1 || numValue > 99) {
      return // Invalid number, don't update
    }

    // Check for duplicates within the same team type
    const teamsOfSameType = teams.filter(t => t.type === team.type && t.id !== teamId)
    const hasDuplicate = teamsOfSameType.some(t => parseInt(t.number) === numValue)
    
    if (hasDuplicate) {
      alert(`Number ${numValue} is already used by another ${team.type.replace('-', ' ')} player`)
      return
    }

    // Update the team number in the store
    updateTeam(teamId, { number: numValue.toString() })
    
    // Update the sprite number text directly in the scene
    if (window.phaserScene && window.phaserScene.updateTeamNumber) {
      window.phaserScene.updateTeamNumber(teamId, numValue.toString())
    }
    
    setEditingNumber(null)
    setNumberInputValue('')
  }

  const startEditingNumber = (teamId, currentNumber) => {
    setEditingNumber(teamId)
    setNumberInputValue(currentNumber)
  }

  const cancelEditingNumber = () => {
    setEditingNumber(null)
    setNumberInputValue('')
  }

  // Group teams by type (home/away)
  const groupedTeams = teams.reduce((acc, team) => {
    const teamType = team.type // 'team-home' or 'team-away'
    if (!acc[teamType]) {
      acc[teamType] = []
    }
    acc[teamType].push(team)
    return acc
  }, {})

  // Toggle group expansion
  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }))
  }

  // Get total animations for a team group
  const getGroupAnimationCount = (teamGroup) => {
    return teamGroup.reduce((count, team) => {
      return count + getTeamAnimations(team.id).length
    }, 0)
  }

  // Get animatable props (soccer balls)
  const animatableProps = props.filter(prop => prop.type === 'soccer-ball')

  // Get total animations for props group
  const getPropsAnimationCount = () => {
    return animatableProps.reduce((count, prop) => {
      return count + getPropAnimations(prop.id).length
    }, 0)
  }

  // Handle duration change with animation trimming
  const handleDurationChange = (newDuration) => {
    // Only check for trimming when reducing duration
    if (newDuration < duration) {
      // Check if any animations exceed the new duration
      const exceedingAnimations = animations.filter(anim => anim.endTime > newDuration)
      
      if (exceedingAnimations.length > 0) {
        // Show warning and ask for confirmation
        const confirmMessage = `Reducing duration to ${newDuration}s will trim ${exceedingAnimations.length} animation(s) that exceed the new duration. Continue?`
        
        if (!confirm(confirmMessage)) {
          // User cancelled - don't change duration
          return
        }
        
        // Trim or remove animations that exceed the new duration
        exceedingAnimations.forEach(anim => {
          // If animation starts after the new duration, remove it entirely
          if (anim.startTime >= newDuration) {
            removeAnimation(anim.id)
            return
          }
          
          // Animation overlaps with new duration - trim it
          // Calculate the new end time (clamp to new duration)
          const newEndTime = newDuration
          
          // Calculate new duration
          const newAnimDuration = newEndTime - anim.startTime
          
          // If animation has a path, trim it to fit the new duration
          let trimmedPath = anim.path || []
          if (trimmedPath.length > 0 && newAnimDuration < anim.duration) {
            // Filter path points that are within the new duration
            const relativeNewEndTime = newAnimDuration
            trimmedPath = trimmedPath.filter(point => {
              const pointTime = point.time ?? 0
              return pointTime <= relativeNewEndTime
            })
            
            // If we have points, update the end position to match the last path point
            if (trimmedPath.length > 0) {
              const lastPoint = trimmedPath[trimmedPath.length - 1]
              const updates = {
                endTime: newEndTime,
                duration: newAnimDuration,
                endX: lastPoint.x ?? anim.endX,
                endY: lastPoint.y ?? anim.endY,
                path: trimmedPath
              }
              updateAnimation(anim.id, updates)
            } else {
              // No path points left, use start position as end
              const updates = {
                endTime: newEndTime,
                duration: newAnimDuration,
                endX: anim.startX,
                endY: anim.startY,
                path: []
              }
              updateAnimation(anim.id, updates)
            }
          } else {
            // No path or simple animation, just update end time and recalculate end position
            // For linear animations, interpolate the end position
            const progress = newAnimDuration / anim.duration
            const updates = {
              endTime: newEndTime,
              duration: newAnimDuration,
              endX: anim.startX + (anim.endX - anim.startX) * progress,
              endY: anim.startY + (anim.endY - anim.startY) * progress
            }
            updateAnimation(anim.id, updates)
          }
        })
      }
    }
    
    // Set the new duration
    setDuration(newDuration)
    
    // If current time exceeds new duration, reset to 0
    if (currentTime > newDuration) {
      setCurrentTime(0)
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-900">Animation Editor</h3>
          {/* Duration Selector */}
          <div className="flex items-center space-x-2">
            <label htmlFor="duration-select" className="text-sm text-gray-600">
              Duration:
            </label>
            <select
              id="duration-select"
              value={duration}
              onChange={(e) => {
                const newDuration = parseInt(e.target.value)
                handleDurationChange(newDuration)
              }}
              className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isPlaying}
            >
              <option value={10}>10s</option>
              <option value={15}>15s</option>
              <option value={20}>20s</option>
              <option value={25}>25s</option>
              <option value={30}>30s</option>
            </select>
          </div>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Create and manage team animations
        </p>
      </div>

      {/* Sticky Status Messages */}
      <div className="sticky top-0 z-10 space-y-2">
        {/* Countdown Status */}
        {isCountdown && (
          <div className="bg-yellow-100 border border-yellow-300 p-3 rounded-lg shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-yellow-700">
                  Recording will start in {countdownValue} second{countdownValue !== 1 ? 's' : ''}...
                </span>
              </div>
              <div className="text-2xl font-bold text-yellow-600">
                {countdownValue}
              </div>
            </div>
            <div className="text-xs text-yellow-600 mt-1">
              Recording animation for {getElementName(selectedTeam || selectedProp)}
            </div>
            <div className="text-xs text-yellow-600 mt-1">
              Timeline start: {typeof recordingTimelineStartTime === 'number' ? recordingTimelineStartTime.toFixed(1) : '0.0'}s
            </div>
            {animations.length > 0 && (
              <div className="text-xs text-yellow-600 mt-1">
                ⏸️ All animations paused during countdown - will resume when recording starts
              </div>
            )}
          </div>
        )}

        {/* Recording Status */}
        {isRecording && (
          <div className="bg-red-100 border border-red-300 p-3 rounded-lg shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-red-700">
                  Recording animation for {getElementName(selectedTeam || selectedProp)}
                </span>
              </div>
              <span className="text-sm font-mono text-red-600">
                {(recordingElapsedTime / 1000).toFixed(1)}s
              </span>
            </div>
             <div className="text-xs text-red-600 mt-1">
               Timeline start: {typeof recordingTimelineStartTime === 'number' ? recordingTimelineStartTime.toFixed(1) : '0.0'}s
             </div>
             <div className="text-xs text-orange-600 mt-1">
               Total duration: {((recordingTimelineStartTime + (recordingElapsedTime / 1000))).toFixed(1)}s / {duration.toFixed(1)}s
             </div>
             <div className="text-xs text-blue-600 mt-1">
               {isPlaying ? '🎬 Existing animations are playing' : '⏸️ No existing animations - timeline paused'}
             </div>
             {(() => {
               const existingAnimations = animations.filter(anim => 
                 selectedProp ? anim.propId === selectedProp : anim.teamId === selectedTeam
               )
               return existingAnimations.length > 0 ? (
                 <div className="text-xs text-green-600 mt-1">
                   🔗 Chaining to existing animation (starts at {typeof recordingTimelineStartTime === 'number' ? recordingTimelineStartTime.toFixed(1) : '0.0'}s)
                 </div>
               ) : null
             })()}
            <button
              onClick={stopRecording}
              className="mt-2 w-full px-3 py-2 text-sm bg-red-500 text-white rounded hover:bg-red-600"
            >
              Stop Recording
            </button>
          </div>
        )}
      </div>

      {/* Current Time Display */}
      <div className="bg-gray-100 p-3 rounded-lg">
        <div className="text-sm text-gray-600">Current Time</div>
        <div className="text-lg font-mono">
          {typeof currentTime === 'number' ? currentTime.toFixed(1) : '0.0'}s
        </div>
      </div>

      {/* Animatable Elements List */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-2">Animatable Elements</h4>
        <div className="space-y-2">
          {/* Teams - Grouped by type */}
          {Object.entries(groupedTeams).map(([teamType, teamGroup]) => {
            const groupKey = `team-${teamType}`
            const isExpanded = expandedGroups[groupKey] !== false // Default to expanded
            const animationCount = getGroupAnimationCount(teamGroup)
            const teamTypeLabel = teamType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())
            
            return (
              <div key={groupKey} className="border rounded-lg border-gray-200">
                {/* Group Header */}
                <button
                  onClick={() => toggleGroup(groupKey)}
                  className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    {isExpanded ? (
                      <ChevronDown size={16} className="text-gray-500" />
                    ) : (
                      <ChevronRight size={16} className="text-gray-500" />
                    )}
                    <span className="text-sm font-semibold text-gray-900">
                      {teamTypeLabel}
                    </span>
                    {animationCount > 0 && (
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                        {animationCount} animation{animationCount !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </button>
                
                {/* Team List - Collapsible */}
                {isExpanded && (
                  <div className="px-3 pb-3 space-y-2">
                    {teamGroup.map((team) => {
                      const teamAnimations = getTeamAnimations(team.id)
                      const isSelected = selectedEntity && selectedEntity.type === 'team' && selectedEntity.id === team.id
                      return (
                        <div 
                          key={team.id} 
                          id={`animatable-element-${team.id}`}
                          className={`border rounded-lg p-3 transition-colors cursor-pointer ${
                            isSelected 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => setSelectedEntity({ type: 'team', id: team.id })}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2 flex-1">
                              <div 
                                className="w-4 h-4 rounded-full flex-shrink-0"
                                style={{ backgroundColor: team.color }}
                              ></div>
                              <span className="text-sm font-medium flex-shrink-0">
                                {team.type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())} #
                              </span>
                              {editingNumber === team.id ? (
                                <div className="flex items-center space-x-1">
                                  <input
                                    type="number"
                                    min="1"
                                    max="99"
                                    value={numberInputValue}
                                    onChange={(e) => setNumberInputValue(e.target.value)}
                                    onBlur={(e) => {
                                      const value = e.target.value.trim()
                                      if (value) {
                                        handleNumberChange(team.id, value)
                                      } else {
                                        cancelEditingNumber()
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.target.blur()
                                      } else if (e.key === 'Escape') {
                                        cancelEditingNumber()
                                      }
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-12 px-1 py-0.5 text-sm border border-blue-500 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    autoFocus
                                  />
                                </div>
                              ) : (
                                <span 
                                  className="text-sm font-medium cursor-pointer hover:text-blue-600 hover:border-blue-400 inline-block px-1.5 py-0.5 border border-gray-300 rounded bg-white min-w-[2rem] text-center"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    startEditingNumber(team.id, team.number)
                                  }}
                                  title="Click to edit number"
                                >
                                  {team.number}
                                </span>
                              )}
                            </div>
                             <div className="flex space-x-1">
                               <button
                                 onClick={(e) => {
                                   e.stopPropagation()
                                   startRecording(team.id)
                                 }}
                                 disabled={isRecording || isCountdown}
                                 className="p-1 text-green-600 hover:bg-green-100 rounded disabled:opacity-50"
                                 title="Record path animation"
                               >
                                 <Play size={14} />
                               </button>
                             </div>
                          </div>
                          
                          {/* Team Animations */}
                          {teamAnimations.length > 0 && (
                            <div className="space-y-1 mt-2">
                              {teamAnimations.map((anim) => (
                                <div key={anim.id} className="flex items-center justify-between bg-gray-50 p-2 rounded text-xs">
                                  <div>
                                    <span className="font-medium">{anim.type}</span>
                                    <span className="text-gray-500 ml-2">
                                      {anim.startTime.toFixed(1)}s - {anim.endTime.toFixed(1)}s
                                    </span>
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      deleteAnimation(anim.id)
                                    }}
                                    className="text-red-600 hover:bg-red-100 p-1 rounded"
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
          
          {/* Props - Grouped */}
          {animatableProps.length > 0 && (
            <div className="border rounded-lg border-gray-200">
              {/* Props Group Header */}
              <button
                onClick={() => toggleGroup('props')}
                className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  {expandedGroups['props'] !== false ? (
                    <ChevronDown size={16} className="text-gray-500" />
                  ) : (
                    <ChevronRight size={16} className="text-gray-500" />
                  )}
                  <span className="text-sm font-semibold text-gray-900">
                    Props
                  </span>
                  {getPropsAnimationCount() > 0 && (
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                      {getPropsAnimationCount()} animation{getPropsAnimationCount() !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </button>
              
              {/* Props List - Collapsible */}
              {expandedGroups['props'] !== false && (
                <div className="px-3 pb-3 space-y-2">
                  {animatableProps.map((prop) => {
                    const propAnimations = getPropAnimations(prop.id)
                    const isSelected = selectedEntity && selectedEntity.type === 'prop' && selectedEntity.id === prop.id
                    return (
                      <div 
                        key={prop.id} 
                        id={`animatable-element-${prop.id}`}
                        className={`border rounded-lg p-3 transition-colors cursor-pointer ${
                          isSelected 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setSelectedEntity({ type: 'prop', id: prop.id })}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <div 
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: prop.color }}
                            ></div>
                            <span className="text-sm font-medium">
                              Soccer Ball
                            </span>
                          </div>
                           <div className="flex space-x-1">
                             <button
                               onClick={(e) => {
                                 e.stopPropagation()
                                 startRecording(prop.id, true)
                               }}
                               disabled={isRecording || isCountdown}
                               className="p-1 text-green-600 hover:bg-green-100 rounded disabled:opacity-50"
                               title="Record path animation"
                             >
                               <Play size={14} />
                             </button>
                           </div>
                        </div>
                        
                        {/* Prop Animations */}
                        {propAnimations.length > 0 && (
                          <div className="space-y-1 mt-2">
                            {propAnimations.map((animation) => (
                              <div key={animation.id} className="flex items-center justify-between text-xs bg-gray-50 p-2 rounded">
                                <span>
                                  {animation.type} ({animation.startTime.toFixed(1)}s - {animation.endTime.toFixed(1)}s)
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    deleteAnimation(animation.id)
                                  }}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <Trash2 size={12} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="flex space-x-2">
        {isRecording ? (
          <button
            onClick={stopRecording}
            className="flex items-center space-x-2 px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600"
          >
            <Square size={14} />
            <span>Stop Recording</span>
          </button>
        ) : (
          <button
            onClick={() => setCurrentTime(0)}
            className="flex items-center space-x-2 px-3 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            <Pause size={14} />
            <span>Reset Time</span>
          </button>
        )}
        
        <button
          onClick={() => {
            if (confirm('Are you sure you want to clear all animations? This will also reset all teams and props to their original positions.')) {
              // Store animations before clearing (we need them to get starting positions)
              const animationsToClear = [...animations]
              
              // Stop playback and reset timeline first
              setPlaying(false)
              setCurrentTime(0)
              
              // Reset Phaser scene's currentTime
              if (window.phaserScene) {
                window.phaserScene.currentTime = 0
                window.phaserScene.currentAnimations = []
              }
              
              // Reset all teams to their original starting positions
              teams.forEach(team => {
                // Find the first animation for this team to get the starting position
                const firstAnimation = animationsToClear
                  .filter(anim => anim.teamId === team.id)
                  .sort((a, b) => a.startTime - b.startTime)[0]
                
                if (firstAnimation) {
                  // Use the starting position from the first animation
                  const restoreX = firstAnimation.startX
                  const restoreY = firstAnimation.startY
                  
                  // Update in store
                  updateTeam(team.id, { 
                    x: restoreX, 
                    y: restoreY 
                  })
                  
                  // Update directly in Phaser scene
                  if (window.phaserScene) {
                    const teamSprite = window.phaserScene.teams.find(t => t.teamData?.id === team.id)
                    if (teamSprite) {
                      teamSprite.x = restoreX
                      teamSprite.y = restoreY
                      
                      // Update number text position
                      if (teamSprite.numberText) {
                        teamSprite.numberText.x = restoreX
                        teamSprite.numberText.y = restoreY
                      }
                      
                      // Update selection border if selected
                      if (teamSprite.selectionBorder) {
                        teamSprite.selectionBorder.clear()
                        teamSprite.selectionBorder.lineStyle(3, 0x00FF00, 1)
                        teamSprite.selectionBorder.strokeCircle(restoreX, restoreY, 20)
                      }
                    }
                  }
                }
              })
              
              // Reset all props to their original starting positions
              props.forEach(prop => {
                // Find the first animation for this prop to get the starting position
                const firstAnimation = animationsToClear
                  .filter(anim => anim.propId === prop.id)
                  .sort((a, b) => a.startTime - b.startTime)[0]
                
                if (firstAnimation) {
                  // Use the starting position from the first animation
                  const restoreX = firstAnimation.startX
                  const restoreY = firstAnimation.startY
                  
                  // Update in store
                  updateProp(prop.id, { 
                    x: restoreX, 
                    y: restoreY 
                  })
                  
                  // Update directly in Phaser scene
                  if (window.phaserScene) {
                    const propSprite = window.phaserScene.props.find(p => p.propData?.id === prop.id)
                    if (propSprite) {
                      propSprite.x = restoreX
                      propSprite.y = restoreY
                      
                      // Update selection border if selected
                      if (propSprite.selectionBorder) {
                        propSprite.selectionBorder.clear()
                        propSprite.selectionBorder.lineStyle(3, 0x00FF00, 1)
                        
                        if (propSprite.propData?.type === 'cone') {
                          propSprite.selectionBorder.strokeCircle(restoreX - 10, restoreY - 13, 18)
                        } else {
                          propSprite.selectionBorder.strokeCircle(restoreX, restoreY, 20)
                        }
                      }
                    }
                  }
                }
              })
              
              // Now clear all animations
              clearAnimations()
            }
          }}
          className="flex items-center space-x-2 px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600"
        >
          <Trash2 size={14} />
          <span>Clear All</span>
        </button>
      </div>

       {/* Instructions */}
       <div className="bg-blue-50 p-3 rounded-lg">
         <h4 className="text-sm font-medium text-blue-900 mb-2">How to Animate:</h4>
         <ul className="text-xs text-blue-800 space-y-1">
           <li>• <strong>Record Path:</strong> Click the play button, then move the player or prop with your mouse</li>
           <li>• <strong>Smart Playback:</strong> Existing animations play while recording new ones</li>
           <li>• <strong>Delete:</strong> Click the trash icon to remove animations</li>
         </ul>
       </div>
    </div>
  )
}

export default AnimationEditor
